/**
 * ユーザー向け GIS 設定 modal (docs/22 要件5)。
 * ハンバーガーメニュー「GIS設定」から開き、レイヤーの 表示 ON/OFF・上下関係・スタイル を
 * ユーザー個別に上書きする (localStorage `cw.gis.overrides.v1.{districtId}`)。
 * 変更は同一タブの地図 overlay インスタンスへ即時反映される (useDistrictGisLayers の event sync)。
 */

import { Button, Checkbox, ColorSwatch, Modal, Select } from '@citizen-walk/shared-ui';
import { useState } from 'react';
import type { GisLayerStyle } from '@citizen-walk/shared-types';
import { useDistrictGisLayers, type GisLayerItem } from './useDistrictGisLayers';

interface Props {
  open: boolean;
  onClose: () => void;
  districtId: string;
  enabled: boolean;
  districtName?: string;
}

export function GisSettingsModal({ open, onClose, districtId, enabled, districtName }: Props) {
  // modal 表示中のみ購読 (buildOverlays=false: items のみ必要、GeoJSON 取得を省略)。
  const gis = useDistrictGisLayers(districtId, open && enabled, { buildOverlays: false });

  return (
    <Modal open={open} onClose={onClose} title="GIS 設定" size="md">
      <p className="mb-3 text-xs text-slate-500">
        {districtName ? `${districtName}の ` : ''}地図に重ねる GIS レイヤーの表示・並び順・見た目を
        調整できます。設定はこの端末に保存されます。
      </p>

      {!gis.hasLayers ? (
        <p className="py-6 text-center text-sm text-slate-500">
          表示できる GIS レイヤーがありません。
        </p>
      ) : (
        <>
          <ul className="grid gap-2">
            {gis.items.map((item, i) => (
              <LayerSettingsRow
                key={item.layer.id}
                item={item}
                isFirst={i === 0}
                isLast={i === gis.items.length - 1}
                onToggle={(v) => gis.setVisible(item.layer.id, v)}
                onMove={(dir) => gis.move(item.layer.id, dir)}
                onStyle={(patch) => gis.setStyle(item.layer.id, patch)}
                onReset={() => gis.resetLayer(item.layer.id)}
              />
            ))}
          </ul>
          <div className="mt-4 flex justify-end">
            <Button size="sm" variant="secondary" onClick={gis.resetAll}>
              すべて既定に戻す
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
}

interface RowProps {
  item: GisLayerItem;
  isFirst: boolean;
  isLast: boolean;
  onToggle: (visible: boolean) => void;
  onMove: (dir: -1 | 1) => void;
  onStyle: (patch: Partial<GisLayerStyle>) => void;
  onReset: () => void;
}

function LayerSettingsRow({ item, isFirst, isLast, onToggle, onMove, onStyle, onReset }: RowProps) {
  const [expanded, setExpanded] = useState(false);
  const { layer, visible, style } = item;
  const gt = layer.geometryType;
  const showFill = gt === 'polygon' || gt === 'mixed';
  const showLine = gt === 'line' || gt === 'polygon' || gt === 'mixed';
  const showPoint = gt === 'point' || gt === 'mixed';

  return (
    <li className="rounded-md border border-slate-200 p-2">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <Checkbox label={layer.name} checked={visible} onChange={onToggle} />
        <span className="ml-auto flex items-center gap-1">
          <IconButton label="上へ" disabled={isFirst} onClick={() => onMove(-1)}>
            ▲
          </IconButton>
          <IconButton label="下へ" disabled={isLast} onClick={() => onMove(1)}>
            ▼
          </IconButton>
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="rounded px-2 py-1 text-xs text-primary-700 hover:bg-primary-50"
          >
            {expanded ? '閉じる' : '見た目'}
          </button>
        </span>
      </div>

      {expanded && (
        <div className="mt-2 grid gap-2 border-t border-slate-100 pt-2">
          {showFill && (
            <>
              <ColorRow
                label="塗り色"
                value={style.fillColor}
                onChange={(v) => onStyle({ fillColor: v })}
              />
              <RangeRow
                label="塗り不透明度"
                value={style.fillOpacity}
                min={0}
                max={1}
                step={0.05}
                onChange={(v) => onStyle({ fillOpacity: v })}
              />
            </>
          )}
          {showLine && (
            <>
              <ColorRow
                label="線色"
                value={style.lineColor}
                onChange={(v) => onStyle({ lineColor: v })}
              />
              <RangeRow
                label="線の太さ"
                value={style.lineWidth}
                min={0}
                max={12}
                step={0.5}
                onChange={(v) => onStyle({ lineWidth: v })}
              />
            </>
          )}
          {showPoint && (
            <>
              <ColorRow
                label="点の色"
                value={style.circleColor}
                onChange={(v) => onStyle({ circleColor: v })}
              />
              <RangeRow
                label="点の大きさ"
                value={style.circleRadius}
                min={1}
                max={24}
                step={1}
                onChange={(v) => onStyle({ circleRadius: v })}
              />
            </>
          )}
          <Checkbox
            label="ラベルを表示"
            checked={style.labelEnabled}
            onChange={(c) => onStyle({ labelEnabled: c })}
          />
          {style.labelEnabled && (
            <>
              <Select
                label="ラベルの属性"
                value={style.labelField ?? ''}
                onChange={(v) => onStyle({ labelField: v === '' ? undefined : v })}
                options={[
                  { value: '', label: '(なし)' },
                  ...layer.attributeFields.map((f) => ({ value: f.key, label: f.key })),
                ]}
              />
              <RangeRow
                label="文字サイズ"
                value={style.labelFontSize}
                min={8}
                max={28}
                step={1}
                onChange={(v) => onStyle({ labelFontSize: v })}
              />
            </>
          )}
          <div className="flex justify-end">
            <button
              type="button"
              onClick={onReset}
              className="text-xs text-slate-500 underline hover:text-slate-700"
            >
              このレイヤーを既定に戻す
            </button>
          </div>
        </div>
      )}
    </li>
  );
}

function IconButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="rounded border border-slate-300 px-2 py-1 text-xs text-slate-600 hover:bg-slate-50 disabled:opacity-40"
    >
      {children}
    </button>
  );
}

function ColorRow({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-2 text-xs text-slate-600">
      <span>{label}</span>
      <ColorSwatch
        value={value}
        onChange={(c) => onChange(c ?? value)}
        showClear={false}
        size={24}
        ariaLabel={`${label} の色`}
      />
    </div>
  );
}

function RangeRow({
  label,
  value,
  min,
  max,
  step,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (v: number) => void;
}) {
  return (
    <label className="flex items-center justify-between gap-2 text-xs text-slate-600">
      <span>{label}</span>
      <span className="flex items-center gap-2">
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className="w-28"
        />
        <span className="w-8 text-right font-mono text-slate-500">{value}</span>
      </span>
    </label>
  );
}
